import type { Icon } from "@studio3t/generic-components";
import type { SlotIntent } from "@/pages/slotIntent";
import type { Encoding, Service } from "@/types/api";
import { NO_EXTRAS, type PipelineExtras } from "./pipelineCode";
import { type PipelineDocError, validatePipelineDoc } from "./pipelineLint";
import type { SchemaRegistryEntry } from "./schemaRefs";
import {
  type StagedResource,
  type StagedRole,
  type StagedSchema,
  schemaRowId,
} from "./stagingModel";

// The inverse of buildPipelineCode (pipelineCode.ts), for VIEWING a
// pipeline that exists only as code — a deployed job, whose stages and
// schemas block become staged rows so the Stages view can show them.
// Best effort by design: whatever the doc can't be staged from is skipped
// and reported as an issue (never thrown). In edit mode the switch to
// Stages only happens on lint-clean code (StudioPipelineProvider's
// prepareStagesSwitch), so the rows never carry problems of their own.
// Encodings, checkpoint, the name, the source's slot and input encoding
// aren't staging data: they ride back to the generator as `extras`.

export interface DerivedStaging {
  sources: StagedResource[];
  transforms: StagedResource[];
  sinks: StagedResource[];
  schemas: StagedSchema[];
  /** Structural problems (validatePipelineDoc's errors) — including every
   *  stage or schema entry skipped for lacking a service_name/resource.
   *  Placeholder entries (all references "") are not problems: they yield
   *  no row and no issue. */
  issues: PipelineDocError[];
  /** Everything the doc carries beyond the staged structure — handed back
   *  to buildPipelineCode so regenerating loses nothing. */
  extras: PipelineExtras;
}

export interface DerivationContext {
  services: ReadonlyArray<{ name: string; provider: Service["provider"] }>;
  registry: ReadonlyArray<SchemaRegistryEntry>;
  /** Row icons — the caller's (the component lib's Icon enum stays out of
   *  this module so it loads without the lib's runtime). */
  icons: {
    resource: (provider: Service["provider"] | undefined) => Icon;
    schema: Icon;
  };
}

type Obj = Record<string, unknown>;
const isObject = (v: unknown): v is Obj => typeof v === "object" && v !== null;
const str = (v: unknown): string | null =>
  typeof v === "string" && v !== "" ? v : null;
const ENCODINGS = new Set<string>(["bson", "json", "avro"]);
const encoding = (v: unknown): Encoding | null =>
  typeof v === "string" && ENCODINGS.has(v) ? (v as Encoding) : null;
const STRUCTURAL_KEYS = new Set(["source", "middlewares", "sinks", "schemas"]);

function stageRow(
  stage: unknown,
  providers: Map<string, Service["provider"]>,
  icons: DerivationContext["icons"],
): StagedResource | null {
  if (!isObject(stage)) return null;
  const serviceName = str(stage.service_name);
  const resource = str(stage.resource);
  if (!serviceName || !resource) return null;
  const provider = providers.get(serviceName);
  const routeId = str(stage.route_id);
  return {
    id: `resource:${serviceName}:${resource}`,
    name: resource,
    icon: icons.resource(provider),
    provider,
    ...(routeId ? { routeId } : {}),
  };
}

// The registry schema a ref names within one service's store (schemas
// live per service, TTTL-319): by id first, then by a name exactly one
// entry carries; null for anything else.
function storeEntry(
  registry: ReadonlyArray<SchemaRegistryEntry>,
  service: string,
  ref: string,
): SchemaRegistryEntry | null {
  const store = registry.filter((e) => e.serviceName === service);
  const byId = store.find((e) => e.id === ref);
  if (byId) return byId;
  const byName = store.filter((e) => e.name === ref);
  return byName.length === 1 ? byName[0] : null;
}

// A schemas-block entry's alias, service and ref — null when any is
// missing (the entry can't stage; the structural lint reports it).
function schemaEntryParts(
  entry: unknown,
): { alias: string; service: string; ref: string } | null {
  if (!isObject(entry)) return null;
  const alias = str(entry.id);
  const service = str(entry.service_name);
  const ref = str(entry.resource);
  if (!alias || !service) return null;
  return ref ? { alias, service, ref } : null;
}

// A schemas-block entry's registry schema, resolved within its own
// service's store. An unresolved ref stays raw — the row still shows (the
// linter's error lands on it via markUnresolved).
function schemaRow(
  entry: unknown,
  registry: ReadonlyArray<SchemaRegistryEntry>,
  icon: Icon,
): { alias: string; row: StagedSchema } | null {
  const parts = schemaEntryParts(entry);
  if (!parts) return null;
  const { alias, service, ref } = parts;
  const resolved = storeEntry(registry, service, ref);
  return {
    alias,
    row: {
      id: schemaRowId(service, resolved?.id ?? ref),
      name: resolved?.name ?? ref,
      icon,
      attachments: [],
    },
  };
}

// The pass's shared state: the service→provider lookup and the row icons
// every stage helper needs, plus rowAt — where each staged entry ended up
// ("source", `middlewares.${i}`, `sinks.${i}` → the row id it staged) —
// and the row ids staged so far, across the roles.
interface StagingPass {
  providers: Map<string, Service["provider"]>;
  icons: DerivationContext["icons"];
  rowAt: Map<string, string>;
  stagedIds: Set<string>;
}

// Staged rows are unique by id across the roles (withStaged, the
// explorer's drag lock), so a resource the doc names again stages nothing:
// its first mention keeps the row, with its encoding and schema. The
// linter reports the repeat (checkRepeatedResources) — it reaches here
// only on the ungated paths, a job or a stored workspace being opened.
function claimRow(
  row: StagedResource | null,
  at: string,
  pass: StagingPass,
): StagedResource | null {
  if (!row || pass.stagedIds.has(row.id)) return null;
  pass.stagedIds.add(row.id);
  pass.rowAt.set(at, row.id);
  return row;
}

const LIST_KEYS = ["middlewares", "sinks"] as const;
type ListKey = (typeof LIST_KEYS)[number];
const ROLE_OF: Record<ListKey, StagedRole> = {
  middlewares: "transform",
  sinks: "sink",
};

/** A doc list, or nothing at all when the field isn't one — a non-list is
 *  reported by the linter, not staged from. */
const listAt = (doc: Obj, key: string): unknown[] => {
  const value = doc[key];
  return Array.isArray(value) ? value : [];
};

function stageSource(doc: Obj, pass: StagingPass): StagedResource[] {
  const row = claimRow(
    stageRow(doc.source, pass.providers, pass.icons),
    "source",
    pass,
  );
  return row ? [row] : [];
}

function stageList(
  doc: Obj,
  key: ListKey,
  pass: StagingPass,
): StagedResource[] {
  const rows: StagedResource[] = [];
  listAt(doc, key).forEach((stage, i) => {
    const row = claimRow(
      stageRow(stage, pass.providers, pass.icons),
      `${key}.${i}`,
      pass,
    );
    if (row) rows.push(row);
  });
  return rows;
}

interface SchemaBlock {
  rows: StagedSchema[];
  /** Each entry's alias (its id) → the row it staged. */
  byAlias: Map<string, StagedSchema>;
}

// Schemas: the block's entries, attached to every stage whose schema_id
// names their alias.
function stageSchemas(
  doc: Obj,
  registry: DerivationContext["registry"],
  pass: StagingPass,
): SchemaBlock {
  const rows: StagedSchema[] = [];
  const byAlias = new Map<string, StagedSchema>();
  listAt(doc, "schemas").forEach((entry, i) => {
    const parsed = schemaRow(entry, registry, pass.icons.schema);
    if (!parsed) return;
    // The same registry schema listed twice folds into one row.
    const existing = rows.find((s) => s.id === parsed.row.id);
    const row = existing ?? parsed.row;
    if (!existing) rows.push(row);
    byAlias.set(parsed.alias, row);
    pass.rowAt.set(`schemas.${i}`, row.id);
  });
  return { rows, byAlias };
}

/** A stage entry that staged a row: the doc's own object next to the row
 *  id and the role it became. */
interface StagedEntry {
  stage: unknown;
  rowId: string;
  role: StagedRole;
}

// The doc's stages in staging order (source, middlewares, sinks), with the
// ones that staged nothing dropped — what's left of the doc still worth
// reading: schema attachments and encodings.
function stagedEntries(doc: Obj, rowAt: Map<string, string>): StagedEntry[] {
  const all = [
    { stage: doc.source, rowId: rowAt.get("source"), role: "source" as const },
    ...LIST_KEYS.flatMap((key) =>
      listAt(doc, key).map((stage, i) => ({
        stage,
        rowId: rowAt.get(`${key}.${i}`),
        role: ROLE_OF[key],
      })),
    ),
  ];
  return all.flatMap(({ stage, rowId, role }) =>
    rowId ? [{ stage, rowId, role }] : [],
  );
}

/** The schemas-block row a stage's schema_id names, if any. */
function schemaFor(
  stage: unknown,
  byAlias: Map<string, StagedSchema>,
): StagedSchema | undefined {
  if (!isObject(stage)) return undefined;
  const alias = str(stage.schema_id);
  return alias ? byAlias.get(alias) : undefined;
}

function attachSchemas(
  entries: StagedEntry[],
  byAlias: Map<string, StagedSchema>,
): void {
  for (const { stage, rowId, role } of entries) {
    const schema = schemaFor(stage, byAlias);
    if (schema && !schema.attachments.some((a) => a.id === rowId)) {
      schema.attachments.push({ role, id: rowId });
    }
  }
}

// Passthrough extras: the other top-level fields as they are, and each
// staged stage's encoding (unknown values are left to the generator's
// provider default).
function passthroughFields(doc: Obj): Record<string, unknown> {
  const fields: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(doc)) {
    if (!STRUCTURAL_KEYS.has(key)) fields[key] = value;
  }
  return fields;
}

// A per-stage encoding key by staged row id; unknown values are left out
// for the generator's provider default. `output_encoding` is read on every
// stage, the source's `input_encoding` (TTTL-268) on the source only.
function passthroughEncodings(
  entries: StagedEntry[],
  key: "output_encoding" | "input_encoding",
  onlyRole?: StagedRole,
): Record<string, Encoding> {
  const encodings: Record<string, Encoding> = {};
  for (const { stage, rowId, role } of entries) {
    if (onlyRole && role !== onlyRole) continue;
    const value = isObject(stage) ? encoding(stage[key]) : null;
    if (value) encodings[rowId] = value;
  }
  return encodings;
}

// A change capture source's slot setting, as the document had it: only the
// source takes one, and a sink's is left for the linter rather than moved.
function passthroughSlots(entries: StagedEntry[]): Record<string, SlotIntent> {
  const slots: Record<string, SlotIntent> = {};
  for (const { stage, rowId, role } of entries) {
    const slot = role === "source" ? slotOf(stage) : undefined;
    if (slot) slots[rowId] = slot;
  }
  return slots;
}

function slotOf(stage: unknown): SlotIntent | undefined {
  if (!isObject(stage)) return undefined;
  return isObject(stage.slot) ? (stage.slot as SlotIntent) : undefined;
}

export function stagingFromPipelineDoc(
  doc: unknown,
  ctx: DerivationContext,
): DerivedStaging {
  // Placeholder entries (all references "") are warnings, not problems:
  // they simply have no row — "nothing staged here" — so they don't count
  // as skipped entries either.
  const issues = validatePipelineDoc(doc).filter(
    (e) => e.severity !== "warning",
  );
  if (!isObject(doc)) {
    return {
      sources: [],
      transforms: [],
      sinks: [],
      schemas: [],
      issues,
      extras: NO_EXTRAS,
    };
  }
  const pass: StagingPass = {
    providers: new Map(ctx.services.map((s) => [s.name, s.provider])),
    icons: ctx.icons,
    rowAt: new Map(),
    stagedIds: new Set(),
  };
  const sources = stageSource(doc, pass);
  const transforms = stageList(doc, "middlewares", pass);
  const sinks = stageList(doc, "sinks", pass);
  const { rows: schemas, byAlias } = stageSchemas(doc, ctx.registry, pass);

  const entries = stagedEntries(doc, pass.rowAt);
  attachSchemas(entries, byAlias);

  return {
    sources,
    transforms,
    sinks,
    schemas,
    issues,
    extras: {
      fields: passthroughFields(doc),
      encodings: passthroughEncodings(entries, "output_encoding"),
      slots: passthroughSlots(entries),
      inputEncodings: passthroughEncodings(entries, "input_encoding", "source"),
    },
  };
}
